import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import moment from "moment";
import {
  Button,
  Col,
  message,
  Popconfirm,
  Popover,
  Row,
  Space,
  Table,
} from "antd";
import { DeleteFilled, EyeOutlined } from "@ant-design/icons";

import sdk from "@/sdk";
import { Contract } from "@/graphql";

import { ContractModel } from "./ContractModal";
import { useCanEdit } from "@/hooks/usePermission";

export function ContractPage() {
  const canEdit = useCanEdit("contracts");
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["contracts"],
    queryFn: () => sdk.getContracts(),
    select: (data) => data.adminContracts,
  });

  const [selectedContract, setSelectedContract] =
    useState<Partial<Contract> | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { isLoading: mutateLoading, mutate } = useMutation({
    mutationKey: ["deleteContract"],
    mutationFn: () => sdk.deleteContract({ name: deleteId ?? "" }),
    onSuccess: () => {
      message.success("Амжилттай устгагдлаа");
      setDeleteId(null);
      refetch();
    },
    onError: (err) => {
      if (err instanceof Error) {
        message.error(err.message);
        setDeleteId(null);
      }
    },
  });

  const { isLoading: recontractLoading, mutate: recontract } = useMutation({
    mutationKey: ["triggerRecontract"],
    mutationFn: () => sdk.triggerRecontract(),
    onSuccess: (data) => {
      message.success(
        `${data.triggerRecontract} хэрэглэгчийн гэрээ шинэчлэгдлээ`,
      );
    },
    onError: (err) => {
      if (err instanceof Error) {
        message.error(err.message);
      }
    },
  });

  const cancelRefund = () => {
    return (
      <Col>
        <Row>Та устгахдаа итгэлтэй байна уу</Row>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: 10,
          }}
        >
          <Button
            type="primary"
            danger
            style={{ width: "49%" }}
            onClick={() => mutate()}
            disabled={mutateLoading}
          >
            Тийм
          </Button>
          <Button
            type="primary"
            style={{ width: "49%" }}
            disabled={mutateLoading}
            onClick={() => setDeleteId(null)}
          >
            Үгүй
          </Button>
        </div>
      </Col>
    );
  };

  return (
    <main className="page">
      <ContractModel
        selectedContract={selectedContract}
        onClose={() => setSelectedContract(null)}
      />
      <div className="page-header">
        <h3>Гэрээ</h3>
        {canEdit && (
          <Space>
            <Popconfirm
              title="Бүх хэрэглэгчийн гэрээг шинэчлэх үү? Хэрэглэгчид дахин гарын үсэг зурах шаардлагатай болно."
              onConfirm={() => recontract()}
              okText="Тийм"
              cancelText="Үгүй"
            >
              <Button danger loading={recontractLoading}>
                Гэрээ шинэчлэх
              </Button>
            </Popconfirm>
            <Button type="primary" onClick={() => setSelectedContract({})}>
              Гэрээ нэмэх
            </Button>
          </Space>
        )}
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table loading={isLoading} dataSource={data} rowKey="name">
            <Table.Column title="Нэр" dataIndex="name" />
            <Table.Column
              title="Багц"
              dataIndex={["portfolio", "localizedName", "mn"]}
              render={(value) => value ?? "Ерөнхий"}
            />
            <Table.Column
              title="Үүсгэсэн огноо"
              dataIndex="createdAt"
              render={(value) => moment(value).format("YYYY.MM.DD HH:mm")}
            />
            <Table.Column
              title="Шинэчлэгдсэн огноо"
              dataIndex="updatedAt"
              render={(value) => moment(value).format("YYYY.MM.DD HH:mm")}
            />
            <Table.Column
              dataIndex="actions"
              render={(_, record: Contract) => (
                <Space>
                  <Button
                    type="default"
                    shape="circle"
                    icon={<EyeOutlined />}
                    onClick={() =>
                      record.previewUrl && window.open(record.previewUrl)
                    }
                  />
                  {canEdit && (
                    <Popover
                      trigger="click"
                      content={cancelRefund}
                      open={deleteId === record.name}
                    >
                      <Button
                        type="primary"
                        shape="circle"
                        danger
                        icon={<DeleteFilled />}
                        onClick={() => setDeleteId(record.name)}
                      />
                    </Popover>
                  )}
                </Space>
              )}
            />
          </Table>
        </div>
      </div>
    </main>
  );
}
